import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';
import {
  faPlug, faSync, faTrash, faPause, faPlay,
  faChartLine, faEye, faMousePointer, faBullseye, faDollarSign, faUsers
} from '@fortawesome/free-solid-svg-icons';

const colors = {
  navy: '#0F1E35',
  navyLight: '#162A4A',
  navyMid: '#1C3459',
  gold: '#C9952A',
  gold2: '#E8B84B',
  muted: '#8FA3BF',
  white: '#FFFFFF',
  green: '#28a745',
  red: '#dc3545',
  yellow: '#ffc107',
};

interface AdAccount {
  id: number;
  fb_account_id: string;
  account_name: string | null;
  account_status: number | null;
  token_expiry: string | null;
  special_ad_category: string | null;
}

interface Campaign {
  id: number;
  campaign_id: string;
  campaign_name: string | null;
  status: string | null;
  objective: string | null;
  daily_budget: string | null;
  lifetime_budget: string | null;
  spend: string | null;
  impressions: number | null;
  clicks: number | null;
  ctr: string | null;
  cpc: string | null;
  actions: any;
}

interface Summary {
  totalCampaigns: number;
  activeCampaigns: number;
  totalSpend: string;
  totalImpressions: number;
  totalClicks: number;
  totalLeads: number;
  avgCtr: string;
}

const FacebookAdsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState('');

  // Check URL params for connection status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      window.history.replaceState({}, '', '/admin/facebook-ads');
    }
    if (params.get('error')) {
      setError(`Connection failed: ${params.get('error')}`);
      window.history.replaceState({}, '', '/admin/facebook-ads');
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/facebook/accounts');
      if (data.success) {
        setAccounts(data.data || []);
        if (data.data?.length > 0 && !selectedAccount) {
          setSelectedAccount(data.data[0].fb_account_id);
        }
      }
    } catch { /* ignore */ }
  }, [selectedAccount]);

  const fetchCampaigns = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await axiosInstance.get(`/facebook/accounts/${selectedAccount}/campaigns`, { params });
      if (data.success) {
        setCampaigns(data.data?.campaigns || []);
        setSummary(data.data?.summary || null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, statusFilter]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleConnect = () => {
    const token = localStorage.getItem('auth_token') || document.cookie.match(/auth_token=([^;]+)/)?.[1] || '';
    window.location.href = `/api/facebook/connect/auth?token=${token}`;
  };

  const handleDisconnect = async (fbAccountId: string) => {
    if (!window.confirm('Disconnect this Facebook ad account?')) return;
    await axiosInstance.delete(`/facebook/accounts/${fbAccountId}`);
    setAccounts(prev => prev.filter(a => a.fb_account_id !== fbAccountId));
    if (selectedAccount === fbAccountId) {
      setSelectedAccount(null);
      setCampaigns([]);
      setSummary(null);
    }
  };

  const handleRefresh = async () => {
    if (!selectedAccount) return;
    setRefreshing(true);
    try {
      await axiosInstance.post(`/facebook/accounts/${selectedAccount}/campaigns/refresh`);
      await fetchCampaigns();
    } catch { /* ignore */ } finally {
      setRefreshing(false);
    }
  };

  const handleToggleStatus = async (campaignId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await axiosInstance.patch(`/facebook/campaigns/${campaignId}/status`, { status: newStatus });
      setCampaigns(prev => prev.map(c => c.campaign_id === campaignId ? { ...c, status: newStatus } : c));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const formatBudget = (val: string | null) => {
    if (!val) return '—';
    return `$${parseFloat(val).toFixed(2)}`;
  };

  const getLeadCount = (actions: any) => {
    if (!actions || !Array.isArray(actions)) return 0;
    const lead = actions.find((a: any) => a.action_type === 'lead');
    return parseInt(lead?.value) || 0;
  };

  const statusBadge = (status: string | null) => {
    const s = status || 'UNKNOWN';
    const color = s === 'ACTIVE' ? colors.green : s === 'PAUSED' ? colors.yellow : colors.red;
    return (
      <span style={{ background: color + '22', color, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
        {s}
      </span>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.navy, color: colors.white, padding: '24px 32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877F2', fontSize: 28 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Facebook Ads Manager</h1>
          <span style={{ background: colors.gold + '22', color: colors.gold, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>KOPS-671</span>
        </div>
        {accounts.length === 0 ? (
          <button onClick={handleConnect} style={btnGold}>
            <FontAwesomeIcon icon={faPlug} style={{ marginRight: 6 }} /> Connect Facebook Ads
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={selectedAccount || ''}
              onChange={e => setSelectedAccount(e.target.value)}
              style={{ background: colors.navyLight, color: colors.white, border: `1px solid ${colors.muted}33`, borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
            >
              {accounts.map(a => (
                <option key={a.fb_account_id} value={a.fb_account_id}>
                  {a.account_name || a.fb_account_id}
                </option>
              ))}
            </select>
            <button onClick={handleRefresh} disabled={refreshing} style={{ ...btnOutline, opacity: refreshing ? 0.5 : 1 }}>
              <FontAwesomeIcon icon={faSync} spin={refreshing} style={{ marginRight: 4 }} /> Refresh
            </button>
            <button onClick={handleConnect} style={btnOutline}>
              <FontAwesomeIcon icon={faPlug} style={{ marginRight: 4 }} /> Add Account
            </button>
            {selectedAccount && (
              <button onClick={() => handleDisconnect(selectedAccount)} style={{ ...btnOutline, borderColor: colors.red, color: colors.red }}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: colors.red + '22', color: colors.red, padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: colors.red, cursor: 'pointer' }}>x</button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 24 }}>
          <StatCard icon={faBullseye} label="Campaigns" value={summary.totalCampaigns} sub={`${summary.activeCampaigns} active`} />
          <StatCard icon={faDollarSign} label="Total Spend" value={`$${summary.totalSpend}`} />
          <StatCard icon={faEye} label="Impressions" value={summary.totalImpressions.toLocaleString()} />
          <StatCard icon={faMousePointer} label="Clicks" value={summary.totalClicks.toLocaleString()} />
          <StatCard icon={faChartLine} label="Avg CTR" value={`${summary.avgCtr}%`} />
          <StatCard icon={faUsers} label="Leads" value={summary.totalLeads} />
        </div>
      )}

      {/* Filter bar */}
      {accounts.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['', 'ACTIVE', 'PAUSED'].map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              style={{
                padding: '6px 16px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: statusFilter === f ? colors.gold : colors.navyLight,
                color: statusFilter === f ? colors.navy : colors.muted,
              }}
            >
              {f || 'All'}
            </button>
          ))}
        </div>
      )}

      {/* Campaign Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.muted }}>Loading campaigns...</div>
      ) : campaigns.length > 0 ? (
        <div style={{ background: colors.navyLight, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.muted}22` }}>
                {['Campaign', 'Status', 'Objective', 'Budget', 'Spend', 'Impr.', 'Clicks', 'CTR', 'CPC', 'Leads', ''].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: colors.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.campaign_id} style={{ borderBottom: `1px solid ${colors.muted}11` }}>
                  <td style={tdStyle}>{c.campaign_name || c.campaign_id}</td>
                  <td style={tdStyle}>{statusBadge(c.status)}</td>
                  <td style={{ ...tdStyle, color: colors.muted }}>{c.objective || '—'}</td>
                  <td style={tdStyle}>{formatBudget(c.daily_budget)}{c.daily_budget ? '/day' : ''}{c.lifetime_budget ? formatBudget(c.lifetime_budget) + ' total' : ''}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{formatBudget(c.spend)}</td>
                  <td style={tdStyle}>{c.impressions?.toLocaleString() || '—'}</td>
                  <td style={tdStyle}>{c.clicks?.toLocaleString() || '—'}</td>
                  <td style={tdStyle}>{c.ctr ? `${parseFloat(c.ctr).toFixed(2)}%` : '—'}</td>
                  <td style={tdStyle}>{c.cpc ? `$${parseFloat(c.cpc).toFixed(2)}` : '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: colors.gold }}>{getLeadCount(c.actions) || '—'}</td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleToggleStatus(c.campaign_id, c.status || '')}
                      style={{ background: 'none', border: 'none', color: c.status === 'ACTIVE' ? colors.yellow : colors.green, cursor: 'pointer', fontSize: 14 }}
                      title={c.status === 'ACTIVE' ? 'Pause' : 'Activate'}
                    >
                      <FontAwesomeIcon icon={c.status === 'ACTIVE' ? faPause : faPlay} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : accounts.length > 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.muted }}>
          No campaigns found. Create campaigns in Facebook Ads Manager and they'll appear here.
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <FontAwesomeIcon icon={faFacebook} style={{ fontSize: 48, color: '#1877F2', marginBottom: 16 }} />
          <h2 style={{ color: colors.white, marginBottom: 8 }}>Connect Your Facebook Ads</h2>
          <p style={{ color: colors.muted, marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
            Link your Facebook ad account to view campaigns, track performance, and manage leads — all from one dashboard.
          </p>
          <button onClick={handleConnect} style={btnGold}>
            <FontAwesomeIcon icon={faPlug} style={{ marginRight: 8 }} /> Connect Facebook Ads
          </button>
        </div>
      )}
    </div>
  );
};

// Stat card component
const StatCard: React.FC<{ icon: any; label: string; value: string | number; sub?: string }> = ({ icon, label, value, sub }) => (
  <div style={{ background: colors.navyLight, borderRadius: 10, padding: '16px 18px', borderLeft: `3px solid ${colors.gold}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <FontAwesomeIcon icon={icon} style={{ color: colors.gold, fontSize: 13 }} />
      <span style={{ color: colors.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>{label}</span>
    </div>
    <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
    {sub && <div style={{ color: colors.muted, fontSize: 11, marginTop: 2 }}>{sub}</div>}
  </div>
);

// Styles
const btnGold: React.CSSProperties = {
  background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
  color: colors.navy, border: 'none', borderRadius: 8, padding: '10px 20px',
  fontWeight: 700, fontSize: 13, cursor: 'pointer',
};

const btnOutline: React.CSSProperties = {
  background: 'none', color: colors.muted, border: `1px solid ${colors.muted}44`,
  borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', color: colors.white,
};

export default FacebookAdsPage;
